import { api } from "@/services/api/createAPI";
import { getAuthToken } from "@/services/supabase/auth/actions/getAuthToken";
import { v2 } from "@pufflig/prompt-studio-types";
import { safeStringify, shortUID } from "@pufflig/prompt-tools";
import { PromptbookWebRuntime } from "@pufflig/promptbook-runtime";
import { useQueryClient } from "@tanstack/react-query";

export const getConnectorTablePreview = async (connectorId: string, tableName: string) => {
  const apiKey = await getAuthToken();
  const { data } = await api(apiKey).post("/api/sql/table_preview", { connectorId, tableName });

  if (!data) {
    return null;
  }

  return data.tablePreview;
};

export const useConnectorTablePreview = (runtime: PromptbookWebRuntime | null) => {
  const queryClient = useQueryClient();

  return async (connectorId: string, tableName: string) => {
    if (!runtime) {
      throw new Error("Promptbook runtime is not initialized");
    }

    const queryKey = ["connector_table_preview", connectorId, tableName];
    const cached = queryClient.getQueryData<v2.TableResource>(queryKey);

    if (cached) {
      return cached;
    }

    const tableResource: v2.TableResource = {
      resourceId: shortUID("p"), // we use a different prefix so we can distinguish between resources created by the user and resources created by the system
      resourceType: "table",
      name: tableName.split(".").pop() || tableName,
      createdAt: new Date().toISOString(),
      isReady: true,
    };

    const data = await getConnectorTablePreview(connectorId, tableName);

    // we save the data in index db so it can be opened by the file viewer
    // we need to make sure to not persist this in the db
    // should probably just save the content as a csv file?
    await runtime.storage.createResource(tableResource, safeStringify(data), false);

    queryClient.setQueryData(queryKey, tableResource);

    return tableResource;
  };
};
